import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CurrencyDollar, Clock, Gift, ArrowUpRight } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";

const DRIVER_IMG = "https://images.pexels.com/photos/7363190/pexels-photo-7363190.jpeg";

const PERKS = [
  { icon: Clock, title: "Flexible schedule", desc: "Drive when you want — mornings, evenings, weekends. You own your time." },
  { icon: CurrencyDollar, title: "Fast payouts", desc: "Get paid weekly with transparent earnings, tips and bonus tracking in-app." },
  { icon: Gift, title: "Referral incentives", desc: "Earn bonuses for every approved driver you bring onto the platform." },
];

export default function DriverSection() {
  return (
    <section className="relative py-24 lg:py-32 border-t border-ih-line" data-testid="home-driver">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-3xl overflow-hidden border border-ih-line2 ih-shadow-neon">
            <img src={DRIVER_IMG} alt="IslandHop independent driver on a motorcycle" className="w-full h-[520px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ih-ink via-ih-ink/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 ih-glass rounded-2xl px-5 py-4 flex items-center justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ih-neon">This week</p>
                <p className="font-display font-extrabold text-2xl text-white">$1,420 <span className="text-sm text-white/50 font-sans font-normal">TTD avg.</span></p>
              </div>
              <span className="px-3 py-1.5 rounded-full bg-ih-neon text-black text-xs font-bold">+18%</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 space-y-8"
        >
          <SectionEyebrow label="For drivers" color="neon" />
          <h2 className="font-display font-extrabold text-4xl lg:text-6xl text-white leading-[1.02]">
            Earn on <span className="text-ih-neon">your</span> schedule.<br />
            <span className="text-white/40">Drive the Caribbean forward.</span>
          </h2>
          <p className="text-white/60 text-lg max-w-xl leading-relaxed">
            Whether you ride a motorcycle, drive a car or run a van — IslandHop turns your wheels
            into income. Fast onboarding, weekly payouts, and the support of a real tech platform behind you.
          </p>

          <div className="grid sm:grid-cols-3 gap-3 pt-2">
            {PERKS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="bg-ih-surface border border-ih-line rounded-2xl p-5">
                  <Icon weight="duotone" size={22} className="text-ih-neon mb-3" />
                  <p className="font-semibold text-white text-sm mb-1">{p.title}</p>
                  <p className="text-xs text-white/55 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Link
              to="/drivers"
              data-testid="driver-section-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ih-neon text-black font-semibold hover:scale-[1.02] transition ih-shadow-neon"
            >
              Become a Driver <ArrowUpRight weight="bold" size={16} />
            </Link>
            <Link to="/drivers" className="text-sm text-white/60 hover:text-white transition underline underline-offset-4 decoration-ih-line2">
              See driver requirements
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
